import React, { useEffect, useState } from "react";
import { client } from "@/lib/api";
import { equipmentSummary } from "@/lib/equipment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LABEL = "font-condensed uppercase tracking-wider text-[11px] font-semibold";
const NONE = "__none__";
export const JOB_TYPE_OPTIONS = [
  ["repair", "Repair"], ["emergency", "Emergency"], ["scheduled_pm", "Scheduled PM"],
  ["warranty", "Warranty"], ["contract", "Contract"], ["quoted_work", "Quoted work"],
];
const PRIORITY_OPTIONS = [["equipment_down", "Equipment down"], ["standard", "Standard"], ["low", "Low"]];

const toLocalInput = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const startValues = job => ({
  description: job.description || "",
  priority: job.priority || "standard",
  job_type: job.job_type || "repair",
  reported_by: job.reported_by || "",
  equipment_id: job.equipment_id || NONE,
  scheduled_at: toLocalInput(job.scheduled_at),
  assigned_tech_id: job.assigned_tech_id || NONE,
});

function changedFields(job, form) {
  const start = startValues(job);
  const body = {};
  Object.keys(form).forEach(key => {
    if (form[key] === start[key]) return;
    if (key === "scheduled_at") body.scheduled_at = form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null;
    else if (key === "equipment_id" || key === "assigned_tech_id") body[key] = form[key] === NONE ? null : form[key];
    else body[key] = form[key];
  });
  return body;
}

function Pick({ label, value, onChange, options, testid }) {
  return (
    <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 mt-1 rounded-[10px] text-[13px]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(([key, text]) => <SelectItem key={key} value={key}>{text}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

export function EditJobDialog({ job, techs, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job) return;
    setForm(startValues(job));
    client.get("/equipment").then(r => setUnits(r.data.filter(e => e.area_id === job.area_id))).catch(() => setUnits([]));
  }, [job]);

  if (!job || !form) return null;
  const set = key => value => setForm(f => ({ ...f, [key]: value }));

  const save = async () => {
    const body = changedFields(job, form);
    if (!Object.keys(body).length) { onClose(); return; }
    const chosen = (techs || []).find(t => t.id === body.assigned_tech_id);
    if (chosen?.site_ids?.length > 0 && !chosen.site_ids.includes(job.site_id)) {
      toast.error(`${chosen.name} is scoped to specific sites — this job's site is outside their scope.`);
      return;
    }
    try {
      setBusy(true);
      await client.patch(`/jobs/${job.id}`, body);
      toast.success(`${job.ticket_number} updated`);
      onDone();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update this job");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[520px] max-h-[88vh] overflow-y-auto" data-testid="edit-job-dialog">
        <DialogHeader>
          <DialogTitle>Edit {job.ticket_number}</DialogTitle>
          <DialogDescription>Change the details taken at intake. Each change is recorded in the audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Problem description</span>
            <Textarea rows={3} className="mt-1 rounded-[10px] text-[13.5px]" value={form.description}
              onChange={e => set("description")(e.target.value)} data-testid="edit-job-description" />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Pick label="Priority" value={form.priority} onChange={set("priority")} options={PRIORITY_OPTIONS} testid="edit-job-priority" />
            <Pick label="Job type" value={form.job_type} onChange={set("job_type")} options={JOB_TYPE_OPTIONS} testid="edit-job-type" />
          </div>
          <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Reported by</span>
            <Input className="mt-1 h-9 rounded-[10px] text-[13px]" value={form.reported_by}
              onChange={e => set("reported_by")(e.target.value)} data-testid="edit-job-reported-by" />
          </label>
          <Pick label="Equipment" value={form.equipment_id} onChange={set("equipment_id")} testid="edit-job-equipment"
            options={[[NONE, "No equipment"], ...units.map(u => [u.id, equipmentSummary(u)])]} />
          <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Scheduled date and time</span>
            <input type="datetime-local" className="mt-1 h-9 w-full rounded-[10px] px-2 text-[13px] bg-white"
              style={{ border: "1px solid #E7EAEE" }} value={form.scheduled_at}
              onChange={e => set("scheduled_at")(e.target.value)} data-testid="edit-job-scheduled" />
          </label>
          <Pick label="Assigned technician" value={form.assigned_tech_id} onChange={set("assigned_tech_id")} testid="edit-job-tech"
            options={[[NONE, "Unassigned"], ...(techs || []).map(t => [t.id, t.name])]} />
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={onClose} data-testid="edit-job-cancel">Cancel</Button>
          <Button className="bf-btn-primary px-4" disabled={busy} onClick={save} data-testid="edit-job-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
